import type { ReviewReportFullDataType } from '@readme/api-contracts/schemas/branch/reviewReport';
import type { BranchReviewStatusRepresentationType } from '@readme/api-contracts/schemas/branch/reviewStatus';

/** Default API `review.summary` when no AI summaries are persisted yet (matches mapper output). */
export const EMPTY_BRANCH_REVIEW_SUMMARY: BranchReviewStatusRepresentationType['summary'] = {
  short: null,
  medium: null,
  long: null,
  updated_at: null,
};

/**
 * Returns true if the review report is in a loading state. When no report
 * exists, it's not considered loading.
 * @example
 * ```ts
 * isReviewReportLoading(null); // false
 * isReviewReportLoading({ status: 'pending' }); // true
 * isReviewReportLoading({ status: 'completed' }); // false
 * ```
 */
export function isReviewReportLoading(report: ReviewReportFullDataType | null): boolean {
  if (!report) return false;
  return report.status === 'pending' || report.status === 'processing';
}

/**
 * Returns true only when the review report is `completed` and appears empty
 * with no errors, warnings or style guide scores. When no report exists, it's
 * considered empty. When report is pending or failed, it's not empty.
 * @example
 * ```ts
 * isReviewReportEmpty(null); // true
 * isReviewReportEmpty({ status: 'pending' }); // false
 * isReviewReportEmpty({ status: 'failed' }); // false
 * isReviewReportEmpty({ stats: { total_errors: 0, total_warnings: 0, average_styleguide_score: 0 } }); // true
 * isReviewReportEmpty({ stats: { total_errors: 1, total_warnings: 0, average_styleguide_score: 1 } }); // false
 * ```
 */
export function isReviewReportEmpty(report: ReviewReportFullDataType | null): boolean {
  if (!report) return true;
  if (report.status !== 'completed') return false;
  const {
    total_errors: totalErrors,
    total_warnings: totalWarnings,
    average_styleguide_score: avgStyleguideScore,
  } = report.stats;
  return !totalErrors && !totalWarnings && !avgStyleguideScore;
}
